import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  KeyRound,
  ShieldOff,
  Check,
  X,
  Loader2,
  ArrowLeft,
  UserPlus,
  Copy,
  UserCheck,
  UserX,
} from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-ZA", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

const relative = (iso) => {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const day = 86400000;
  if (diff < day) return "Today";
  const days = Math.floor(diff / day);
  if (days === 1) return "1 day ago";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? "1 month ago" : `${months} months ago`;
};

export default function ManageUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { type, user }
  const [busy, setBusy] = useState(false);
  const [tempResult, setTempResult] = useState(null); // { email, temporary_password, note }

  const loadUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/users`, { withCredentials: true });
      setUsers(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load users");
      setUsers([]);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const runAction = async () => {
    if (!confirmAction) return;
    const { type, user } = confirmAction;
    setBusy(true);
    try {
      if (type === "reset-password") {
        const { data } = await axios.post(
          `${API}/auth/users/${user.id}/reset-password`,
          {},
          { withCredentials: true }
        );
        if (data.emailed) {
          toast.success(`Temporary password emailed to ${data.email}. Account disabled until used.`);
        } else {
          setTempResult(data);
        }
      } else if (type === "reset-mfa") {
        const { data } = await axios.post(
          `${API}/auth/users/${user.id}/reset-mfa`,
          {},
          { withCredentials: true }
        );
        toast.success(`MFA reset for ${data.email}. They'll set up MFA again on next login.`);
      } else if (type === "approve-signup") {
        const { data } = await axios.post(
          `${API}/auth/users/${user.id}/approve-signup`,
          {},
          { withCredentials: true }
        );
        toast.success(`${data.email} approved. They can now sign in.`);
      } else if (type === "reject-signup") {
        const { data } = await axios.post(
          `${API}/auth/users/${user.id}/reject-signup`,
          {},
          { withCredentials: true }
        );
        toast.success(`Sign up request for ${data.email} rejected.`);
      }
      await loadUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Action failed");
    } finally {
      setBusy(false);
      setConfirmAction(null);
    }
  };

  const confirmCopy = {
    "reset-password": {
      title: "Reset password?",
      desc: (u) =>
        `This will disable ${u?.name || u?.email}'s account, sign them out immediately, and email them a temporary password. Their account re-activates when they sign in with the temporary password and set a new one.`,
      action: "Reset password",
    },
    "reset-mfa": {
      title: "Reset MFA?",
      desc: (u) =>
        `This will turn off two-factor authentication for ${u?.name || u?.email} and sign them out immediately. They will be asked to scan a new QR code on their next login.`,
      action: "Reset MFA",
    },
    "approve-signup": {
      title: "Approve sign up?",
      desc: (u) => `This will activate ${u?.name || u?.email}'s account. They'll be able to sign in and set up MFA.`,
      action: "Approve",
    },
    "reject-signup": {
      title: "Reject sign up?",
      desc: (u) => `This will permanently delete the sign up request for ${u?.name || u?.email}.`,
      action: "Reject",
    },
  };

  return (
    <div className="min-h-full bg-slate-50 px-8 py-8" data-testid="manage-users-page">
      {/* Back link */}
      <button
        onClick={() => navigate("/")}
        data-testid="go-back-link"
        className="flex items-center gap-1.5 text-sm font-medium text-[#13B5EA] hover:underline mb-6"
      >
        <ArrowLeft size={15} /> Go back to TC Auditors (Pty) Ltd
      </button>

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-[#1e2d38]">Users</h1>
        <button
          onClick={() => navigate("/manage-users/add")}
          data-testid="add-user-button"
          className="flex items-center gap-2 rounded-full bg-[#13B5EA] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#0f9fce] transition-colors"
        >
          <UserPlus size={16} /> Add User
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-slate-500">
                <th className="px-5 py-3 font-semibold">E-mail</th>
                <th className="px-5 py-3 font-semibold">Name</th>
                <th className="px-5 py-3 font-semibold">Last Activity</th>
                <th className="px-5 py-3 font-semibold text-center">2FA?</th>
                <th className="px-5 py-3 font-semibold">Date Created</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users === null && (
                <tr>
                  <td colSpan={7} className="px-5 py-10 text-center text-slate-400">
                    <Loader2 className="animate-spin inline mr-2" size={18} /> Loading users…
                  </td>
                </tr>
              )}
              {users?.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-10 text-center text-slate-400">
                    No users found.
                  </td>
                </tr>
              )}
              {users?.map((u) => (
                <tr
                  key={u.id}
                  className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors"
                  data-testid={`user-row-${u.email}`}
                >
                  <td className="px-5 py-4 text-[#13B5EA] font-medium">{u.email}</td>
                  <td className="px-5 py-4 text-slate-700">{u.name || "—"}</td>
                  <td className="px-5 py-4 text-slate-500">{relative(u.last_activity)}</td>
                  <td className="px-5 py-4 text-center">
                    {u.mfa_enabled ? (
                      <Check className="inline text-emerald-500" size={18} />
                    ) : (
                      <X className="inline text-slate-300" size={18} />
                    )}
                  </td>
                  <td className="px-5 py-4 text-slate-500">{fmtDate(u.created_at)}</td>
                  <td className="px-5 py-4">
                    {u.pending_approval ? (
                      <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-600">
                        Pending Approval
                      </span>
                    ) : u.disabled ? (
                      <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-1 text-xs font-medium text-red-600">
                        Disabled
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-600">
                        Active
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-2">
                      {u.pending_approval ? (
                        <>
                          <button
                            onClick={() => setConfirmAction({ type: "approve-signup", user: u })}
                            data-testid={`approve-signup-${u.email}`}
                            className="flex items-center gap-1.5 rounded-lg border border-emerald-500 px-3 py-1.5 text-xs font-semibold text-emerald-600 hover:bg-emerald-500 hover:text-white transition-colors"
                          >
                            <UserCheck size={14} /> Approve
                          </button>
                          <button
                            onClick={() => setConfirmAction({ type: "reject-signup", user: u })}
                            data-testid={`reject-signup-${u.email}`}
                            className="flex items-center gap-1.5 rounded-lg border border-red-500 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-500 hover:text-white transition-colors"
                          >
                            <UserX size={14} /> Reject
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => setConfirmAction({ type: "reset-password", user: u })}
                            data-testid={`reset-password-${u.email}`}
                            className="flex items-center gap-1.5 rounded-lg border border-[#13B5EA] px-3 py-1.5 text-xs font-semibold text-[#13B5EA] hover:bg-[#13B5EA] hover:text-white transition-colors"
                          >
                            <KeyRound size={14} /> Reset Password
                          </button>
                          <button
                            onClick={() => setConfirmAction({ type: "reset-mfa", user: u })}
                            data-testid={`reset-mfa-${u.email}`}
                            className="flex items-center gap-1.5 rounded-lg border border-amber-500 px-3 py-1.5 text-xs font-semibold text-amber-600 hover:bg-amber-500 hover:text-white transition-colors"
                          >
                            <ShieldOff size={14} /> Reset MFA
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Confirm dialog */}
      <AlertDialog open={!!confirmAction} onOpenChange={(o) => !o && setConfirmAction(null)}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction && confirmCopy[confirmAction.type].title}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction && confirmCopy[confirmAction.type].desc(confirmAction.user)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel" disabled={busy}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                runAction();
              }}
              data-testid="confirm-proceed"
              disabled={busy}
              className="bg-[#13B5EA] hover:bg-[#0f9fce]"
            >
              {busy && <Loader2 size={15} className="animate-spin mr-2" />}
              {confirmAction && confirmCopy[confirmAction.type].action}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Temp password result (shown when email not configured) */}
      <Dialog open={!!tempResult} onOpenChange={(o) => !o && setTempResult(null)}>
        <DialogContent data-testid="temp-password-dialog">
          <DialogHeader>
            <DialogTitle>Temporary password generated</DialogTitle>
            <DialogDescription>
              {tempResult?.note ||
                "Share this temporary password with the user securely. Their account is disabled until they sign in with it."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">
              For <span className="font-medium text-slate-700">{tempResult?.email}</span>
            </p>
            <div className="flex items-center gap-2">
              <code
                data-testid="temp-password-value"
                className="flex-1 rounded-lg bg-slate-100 border border-slate-200 px-3 py-2 font-mono text-sm text-slate-800 break-all"
              >
                {tempResult?.temporary_password}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(tempResult?.temporary_password || "");
                  toast.success("Copied");
                }}
                className="flex items-center justify-center h-9 w-9 rounded-lg border border-slate-200 text-slate-500 hover:text-[#13B5EA] hover:border-[#13B5EA] transition-colors"
                data-testid="copy-temp-password"
                aria-label="Copy"
              >
                <Copy size={16} />
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
